import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ReactFlow, Handle, Position, Background, NodeProps, applyNodeChanges, NodeChange } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { IOrganizationChartNode } from '../../../../app/resource/node/node.types';
import { CustomNode } from './CustomNode';
import { useSelect, useSelectWithParams } from '../../../../utils/selector.utils';
import { authSelectors } from '../../../../app/auth/auth.selectors';
import { nodeSelectors } from '../../../../app/resource/node/node.selectors';
import { updateNodesPositions } from '../../../../app/page/page-storage/node/node.actions';
import { IDefaultChartProps } from './types';
import {
  buildDragPositionSnapshot,
  diffNodePositions,
  DragPositionSnapshot,
  alignSiblingYForDraggedNode,
  isMultiNodeDragPositionBatch,
  mergeNodesPreservingMeasured,
  snapDraggedNodeXToPrimaryParentIfClose,
} from './utils/drag.utils';
import { getChartLayoutFingerprint } from './utils/chartLayoutFingerprint';
import { buildReactFlowNodesAndEdges, OrgChartNodeType } from './utils/reactFlowGraph';

export const DefaultChart: FunctionComponent<IDefaultChartProps> = ({ nodeIds, chartType, readOnly = false }) => {
  const dispatch = useDispatch();
  const isAdmin = useSelect(authSelectors.selectUserIsAdmin);
  const editable = isAdmin && !readOnly;

  const OrgChartNode = useCallback(
    ({ data }: NodeProps<OrgChartNodeType>) => (
      <>
        <Handle type="target" position={Position.Top} style={{ visibility: 'hidden' }} />
        <CustomNode nodeData={data.nodeData} chartType={data.chartType} readOnly={readOnly} />
        <Handle type="source" position={Position.Bottom} style={{ visibility: 'hidden' }} />
      </>
    ),
    [readOnly],
  );

  const nodeTypes = useMemo(() => ({ orgChartNode: OrgChartNode }), [OrgChartNode]);
  const rawNodes = useSelectWithParams(nodeSelectors.selectResourcesById, nodeIds) as (IOrganizationChartNode | null)[];
  const nodes = rawNodes.filter((n): n is IOrganizationChartNode => n != null);

  const { rfNodes, rfEdges } = useMemo(() => buildReactFlowNodesAndEdges(nodes, chartType), [nodes, chartType]);

  /** Stable only when chart data from Redux actually changes (not when array identity changes). */
  const chartLayoutSyncKey = useMemo(() => getChartLayoutFingerprint(chartType, nodeIds, nodes), [
    chartType,
    nodeIds,
    nodes,
  ]);

  const [displayNodes, setDisplayNodes] = useState(rfNodes);
  const rfNodesRef = useRef(rfNodes);
  rfNodesRef.current = rfNodes;
  const displayNodesRef = useRef(displayNodes);
  displayNodesRef.current = displayNodes;
  const dragPositionsSnapshotRef = useRef<DragPositionSnapshot | null>(null);

  useEffect(() => {
    setDisplayNodes((prevNodes) => mergeNodesPreservingMeasured(rfNodesRef.current, prevNodes));
  }, [chartLayoutSyncKey]);

  const onNodesChange = useCallback((changes: NodeChange<OrgChartNodeType>[]) => {
    const skipOrgChartDragAdjustments = isMultiNodeDragPositionBatch(changes);

    setDisplayNodes((nds) => {
      let nextNodes = applyNodeChanges(changes, nds);

      if (skipOrgChartDragAdjustments) {
        return nextNodes;
      }

      for (const change of changes) {
        // Only user-driven drags set `dragging` (true while moving, false on release). Skip layout/resize
        // position updates where `dragging` is omitted.
        if (change.type !== 'position' || change.position === undefined || change.dragging === undefined) {
          continue;
        }

        const changeId = String(change.id);
        nextNodes = snapDraggedNodeXToPrimaryParentIfClose(nextNodes, changeId);
        const draggedNode = nextNodes.find((node) => node.id === changeId);
        if (!draggedNode) {
          continue;
        }
        nextNodes = alignSiblingYForDraggedNode(nextNodes, draggedNode);
      }

      return nextNodes;
    });
  }, []);

  const onNodeDragStart = useCallback((_event: React.MouseEvent, _node: OrgChartNodeType) => {
    dragPositionsSnapshotRef.current = buildDragPositionSnapshot(displayNodesRef.current);
  }, []);

  /** Ensures snap runs even if a `onNodesChange` batch omits `dragging`; does not merge RF's unsnapped `node.position`. */
  const onNodeDrag = useCallback((_event: React.MouseEvent, node: OrgChartNodeType) => {
    if (displayNodesRef.current.filter((n) => n.selected).length > 1) {
      return;
    }
    const id = String(node.id);
    setDisplayNodes((nds) => {
      const next = snapDraggedNodeXToPrimaryParentIfClose(nds, id);
      const dragged = next.find((n) => n.id === id);
      return dragged ? alignSiblingYForDraggedNode(next, dragged) : next;
    });
  }, []);

  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent, draggedNode: OrgChartNodeType, flowNodes: OrgChartNodeType[]) => {
      if (!editable) {
        return;
      }
      const snapshot = dragPositionsSnapshotRef.current;
      dragPositionsSnapshotRef.current = null;
      if (!snapshot) {
        return;
      }
      let finalNodes = displayNodesRef.current;
      if (flowNodes.length > 1) {
        for (const n of flowNodes) {
          finalNodes = snapDraggedNodeXToPrimaryParentIfClose(finalNodes, String(n.id));
        }
      } else {
        finalNodes = snapDraggedNodeXToPrimaryParentIfClose(finalNodes, String(draggedNode.id));
        const draggedAfterSnap = finalNodes.find((n) => n.id === String(draggedNode.id)) ?? draggedNode;
        finalNodes = alignSiblingYForDraggedNode(finalNodes, draggedAfterSnap);
      }
      setDisplayNodes(finalNodes);

      const payload = diffNodePositions(snapshot, finalNodes);
      if (payload.length > 0) {
        dispatch(updateNodesPositions(chartType, payload));
      }
    },
    [chartType, dispatch, editable],
  );

  return (
    <div
      className="orgchart elements h100"
      style={{ width: '100%', height: '100%', minHeight: 0, position: 'relative' }}
    >
      <ReactFlow
        nodes={displayNodes}
        edges={rfEdges}
        nodeTypes={nodeTypes}
        nodesDraggable={editable}
        onNodesChange={onNodesChange}
        onNodeDragStart={onNodeDragStart}
        onNodeDrag={onNodeDrag}
        onNodeDragStop={onNodeDragStop}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        minZoom={0.1}
        selectionOnDrag={editable}
        panOnDrag={editable ? [1, 2] : true}
      >
        <Background color="#e8e8e8" gap={20} />
      </ReactFlow>
    </div>
  );
};
